import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert, Table, Spinner } from 'react-bootstrap';
import { StoreOperatorGateway } from '../../gateways/StoreOperatorGateway';
import { FireStoreOperator } from '../../model/OperatorModel';
import { CountryCodes } from '../../model/UserModel';
import PasswordInput from '../common/PasswordInput';

type Errors = {
    firstName?: string;
    lastName?: string;
    email?: string;
    confirmEmail?: string;
    phoneCode?: string;
    phoneNumber?: string;
    password?: string;
};

const RegisterStoreOperator: React.FC = () => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [phoneCode, setPhoneCode] = useState('+27');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<Errors>({});
    const [apiError, setApiError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [operators, setOperators] = useState<FireStoreOperator[]>([]);

    const validateForm = (): boolean => {
        let newErrors: Errors = {};

        if (!firstName.trim()) newErrors.firstName = 'First Name is required';
        if (!lastName.trim()) newErrors.lastName = 'Last Name is required';
        if (!phoneCode.trim()) newErrors.phoneCode = 'Phone code is required';

        if (!phoneNumber.trim()) {
            newErrors.phoneNumber = 'Phone number is required';
        } else if (!phoneNumber.match(/^\d{2,3}\d{3}\d{4}$/)) {
            newErrors.phoneNumber = 'Invalid phone number (valid e.g 0821101010)';
        }

        if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            newErrors.email = 'Invalid email format';
        }

        if (confirmEmail !== email) {
            newErrors.confirmEmail = 'Emails do not match';
        }

        if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters long';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const fetchOperators = async () => {
        const list = await StoreOperatorGateway.Get();
        setOperators(list);
    };

    useEffect(() => {
        fetchOperators();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setApiError(null);
        setSuccessMessage(null);

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            await StoreOperatorGateway.Save({ firstName, lastName, email, phoneCode, phoneNumber, password })

            setSuccessMessage('Saved Successfully');
            setFirstName('');
            setLastName('');
            setEmail('');
            setConfirmEmail('');
            setPhoneCode('');
            setPhoneNumber('');
            setPassword('');
            setErrors({});
            fetchOperators();
            setTimeout(() => {
                setSuccessMessage(null);
            }, 2000);
        } catch (err: any) {
            setApiError(err.message);
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await StoreOperatorGateway.Delete(id);
            setOperators((prev) => prev.filter((op) => op.id !== id));
        } catch (err: any) {
            setApiError(`Failed to delete operator: ${err.message}`);
        }
    };

    return (
        <Container className="mt-5">
            <h4>Existing Store Operators</h4>
            {operators.length === 0 ? (
                <p>No operators found.</p>
            ) : (
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {operators.map((op) => (
                            <tr key={op.id}>
                                <td>{op.operator.firstName} {op.operator.lastName}</td>
                                <td>{op.operator.email}</td>
                                <td>
                                    <Button variant="danger" size="sm" onClick={() => handleDelete(op.id)}>
                                        Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
            <hr />
            <h4>Create Store Operator</h4>
            {apiError && <Alert variant="danger">{apiError}</Alert>}
            <Form onSubmit={handleSubmit}>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                isInvalid={!!errors.firstName}
                            />
                            <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                isInvalid={!!errors.lastName}
                            />
                            <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                isInvalid={!!errors.email}
                            />
                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Confirm Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={confirmEmail}
                                onChange={(e) => setConfirmEmail(e.target.value)}
                                isInvalid={!!errors.confirmEmail}
                            />
                            <Form.Control.Feedback type="invalid">{errors.confirmEmail}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone Number</Form.Label>
                            <Row className="g-2">
                                <Col xs={12} md={4}>
                                    <Form.Select
                                        value={phoneCode}
                                        name="phoneCode"
                                        onChange={(e) => setPhoneCode(e.target.value)}
                                    >
                                        {CountryCodes.map((country, index) => (
                                            <option key={index} value={country.code}>
                                                {country.name} ({country.code})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs={12} md={8}>
                                    <Form.Control
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        isInvalid={!!errors.phoneNumber}
                                        placeholder='Enter your phone number'
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.phoneNumber}
                                    </Form.Control.Feedback>
                                </Col>
                            </Row>
                        </Form.Group>
                    </Col>
                </Row>


                <Form.Group className="mb-3">
                    <PasswordInput label='Password' value={password} onChange={(e) => setPassword(e)} isInvalid={!!errors.password}></PasswordInput>
                </Form.Group>

                <Button variant="primary" type="submit">
                    {isSubmitting ? <Spinner animation="border" size="sm" /> : "Register Operator"}</Button>
                {successMessage && <Alert variant="success" className="mb-0 mt-1 py-1 px-2">
                    {successMessage}
                </Alert>}
            </Form>
        </Container>
    );
};

export default RegisterStoreOperator;